import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { BacklogItem } from '../../types/project.js';
import * as projectApi from '../../services/project-api.js';

@customElement('cp-backlog-list')
export class CpBacklogList extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) projectId = 0;

  @state() private _items: BacklogItem[] = [];
  @state() private _loading = false;
  @state() private _error = '';

  updated(changed: Map<string, unknown>): void {
    if (changed.has('projectId') && this.projectId > 0) {
      void this._loadBacklog();
    }
  }

  private async _loadBacklog(): Promise<void> {
    if (!this.projectId) return;
    this._loading = true;
    this._error = '';

    try {
      this._items = await projectApi.getBacklog(this.projectId);
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Backlog konnte nicht geladen werden';
    } finally {
      this._loading = false;
    }
  }

  private _getPriorityClass(priority: string): string {
    switch (priority) {
      case 'critical': return 'cp-backlog-list__priority--critical';
      case 'high': return 'cp-backlog-list__priority--high';
      case 'medium': return 'cp-backlog-list__priority--medium';
      case 'low': return 'cp-backlog-list__priority--low';
      default: return '';
    }
  }

  private _getStatusLabel(status: string): string {
    switch (status) {
      case 'ready': return 'Offen';
      case 'in_progress': return 'In Arbeit';
      case 'done': return 'Erledigt';
      default: return status;
    }
  }

  private _isHighPriority(priority: string): boolean {
    return priority === 'high' || priority === 'critical';
  }

  private _renderSkeleton() {
    return html`
      <div class="cp-backlog-list__items">
        ${[1, 2, 3].map(() => html`
          <div class="cp-backlog-list__skeleton">
            <div class="cp-backlog-list__skeleton-line cp-backlog-list__skeleton-line--title"></div>
            <div class="cp-backlog-list__skeleton-line cp-backlog-list__skeleton-line--meta"></div>
          </div>
        `)}
      </div>
    `;
  }

  private _renderEmpty() {
    return html`
      <div class="cp-backlog-list__empty">
        <p class="cp-backlog-list__empty-text">Kein Backlog vorhanden</p>
      </div>
    `;
  }

  private _renderItem(item: BacklogItem) {
    const highlighted = this._isHighPriority(item.priority);
    return html`
      <div class="cp-backlog-list__item ${highlighted ? 'cp-backlog-list__item--highlighted' : ''}">
        <div class="cp-backlog-list__item-header">
          <span class="cp-backlog-list__item-id">${item.id}</span>
          <span class="cp-backlog-list__priority ${this._getPriorityClass(item.priority)}">${item.priority}</span>
        </div>
        <p class="cp-backlog-list__item-title">${item.title}</p>
        <div class="cp-backlog-list__item-meta">
          <span class="cp-backlog-list__item-type">${item.type}</span>
          <span class="cp-backlog-list__item-status">${this._getStatusLabel(item.status)}</span>
        </div>
        ${item.description ? html`
          <p class="cp-backlog-list__item-desc">${item.description}</p>
        ` : ''}
      </div>
    `;
  }

  render() {
    if (this._loading) return this._renderSkeleton();

    if (this._error) {
      return html`<p class="cp-backlog-list__error">${this._error}</p>`;
    }

    if (this._items.length === 0) return this._renderEmpty();

    return html`
      <div class="cp-backlog-list">
        <div class="cp-backlog-list__items">
          ${this._items.map(item => this._renderItem(item))}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-backlog-list': CpBacklogList;
  }
}
